import React, { useRef, useMemo } from "react";
import { useBonus } from "../context/BonusContext";
import { useStatic } from "../context/StaticContext";
import { getUserIdFromUrl } from "../helper/apiHelper";
import { useTranslation } from "../hooks/useTranslation";
import { FiGift, FiTarget, FiStar, FiCommand } from "react-icons/fi";
import { BiCategoryAlt, BiFootball } from "react-icons/bi";
import { MdOutlineAttachMoney } from "react-icons/md";
import { RiGamepadLine, RiCoinLine } from "react-icons/ri";
import { IoTrophyOutline } from "react-icons/io5";
import { HiOutlineShoppingBag } from "react-icons/hi";
import { GiPokerHand } from "react-icons/gi";
import { FaDharmachakra } from "react-icons/fa";
import BannerCarousel from "./BannerCarousel";
import { useCss } from "../context/CssContext";
// Badge component for showing item counts

const Badge = ({ count }) => {
  if (!count || count <= 0) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: "0px",
        right: "5px",
        backgroundColor: "var(--badge-bg-color, #ff4757)",
        color: "var(--badge-text-color, #ffffff)",
        borderRadius: "12px",
        padding: "0 6px",
        fontSize: "11px",
        minWidth: "16px",
        height: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        zIndex: 10,
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        transform: "translate(50%, -50%)",
        pointerEvents: "none",
      }}
    >
      {count}
    </div>
  );
};

const SubCategoryMenu = ({
  tabsContainerRef,
  formattedTabs,
  activeTabs,
  getSubCategoryIcon,
  handleTabChange,
}) => {
  const { tabGoldMode } = useCss();
  return (
    <div className="sub-category-menu" style={{ overflow: "visible" }}>
      <div
        className="tabs-container"
        ref={tabsContainerRef}
        style={{ overflow: "visible" }}
      >
        {formattedTabs.length > 0 ? (
          formattedTabs.map((tab) => (
            <div
              key={tab.id}
              style={{ position: "relative", display: "inline-flex", overflow: "visible" }}
            >
              <button
                className={`tab-button ${activeTabs.includes(tab.id) ? "active" : ""} ${tabGoldMode ? "gold-theme" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <span className="tab-icon">
                  {getSubCategoryIcon(tab.id)}
                </span>
                <span className="tab-label">{tab.label}</span>
              </button>
              <Badge count={tab.count} />
            </div>
          ))
        ) : (
          <div className="tabs-loading">Yükleniyor...</div>
        )}
      </div>
    </div>
  );
};

const TabMenu = () => {
  const { tabs, bonusData, gamesData, loading, gamesLoading } = useBonus();
  const { buttonURLs, activeTabs, setActiveTabs, activeMainCategory, setActiveMainCategory } = useStatic();
  const { tTab } = useTranslation();
  const { tabGoldMode } = useCss();
  const tabsContainerRef = useRef(null);

  // Tüm veriler yüklenene kadar bekle
  const allDataLoaded = !loading && !gamesLoading;

  // Sabit ana kategoriler (Figma tasarımındaki gibi)
  const { isMainCategoriesActive, mainCategories } = useMemo(() => {
    const baseCategories = [
      {
        id: "bonuslar",
        label: tTab("bonuslar", "Bonuslar"),
        icon: <FiGift size={20} />,
      },
    ];

    if (allDataLoaded && gamesData && gamesData.length > 0) {
      baseCategories.push({
        id: "oyunlar",
        label: tTab("oyunlar", "Oyunlar"),
        icon: <RiGamepadLine size={20} />,
      });
    }

    // Ana kategori menüsünü aktif et eğer:
    const hasExternalUrls =
      (buttonURLs?.lotteryURL && buttonURLs.lotteryURL.trim() !== '') ||
      (buttonURLs?.pointShopURL && buttonURLs.pointShopURL.trim() !== '') ||
      (buttonURLs?.spinWheelURL && buttonURLs.spinWheelURL.trim() !== '') ||
      (buttonURLs?.shakeWinURL && buttonURLs.shakeWinURL.trim() !== '') ||
      (buttonURLs?.scratchURL && buttonURLs.scratchURL.trim() !== '');

    const shouldShowMainCategories = allDataLoaded && (
      (gamesData && gamesData.length > 0) || hasExternalUrls
    );

    let allCategories = [...baseCategories];

    // Helper function to safely parse and add URL tabs
    const addUrlTabs = (urlValue, baseId, baseLabel, iconComp) => {
      if (!urlValue || urlValue.trim() === '') return;
      try {
        const arr = JSON.parse(urlValue);
        if (Array.isArray(arr)) {
          arr.forEach((item, index) => {
            if (item.url && item.url.trim() !== '') {
              allCategories.push({
                id: `${baseId}_${index}`,
                label: item.name || baseLabel,
                icon: iconComp,
                buttonURL: item.url,
                isExternal: true,
              });
            }
          });
        } else {
          // If JSON but not array, fallback to old single item logic
          allCategories.push({
            id: baseId,
            label: baseLabel,
            icon: iconComp,
            buttonURL: urlValue,
            isExternal: true,
          });
        }
      } catch (e) {
        // Not a JSON string, fallback to old single item logic
        allCategories.push({
          id: baseId,
          label: baseLabel,
          icon: iconComp,
          buttonURL: urlValue,
          isExternal: true,
        });
      }
    };

    addUrlTabs(buttonURLs?.lotteryURL, "cekilis", (buttonURLs?.lotteryBtnName && buttonURLs.lotteryBtnName.trim() !== '') ? buttonURLs.lotteryBtnName : "Çekiliş", <IoTrophyOutline size={20} />);
    addUrlTabs(buttonURLs?.spinWheelURL, "cark", tTab("cark", "Çark"), <FaDharmachakra size={20} />);
    addUrlTabs(buttonURLs?.shakeWinURL, "sallakazan", tTab("sallakazan", "Salla Kazan"), <RiCoinLine size={20} />);
    addUrlTabs(buttonURLs?.scratchURL, "silsupur", tTab("silsupur", "Silsüpür"), <FiStar size={20} />);
    addUrlTabs(buttonURLs?.pointShopURL, "puanmarket", tTab("puanmarket", "Puan Market"), <HiOutlineShoppingBag size={20} />);

    return {
      isMainCategoriesActive: shouldShowMainCategories,
      mainCategories: allCategories,
    };
  }, [buttonURLs, gamesData, tTab, allDataLoaded]);

  // Alt kategoriler için icon eşleştirmeleri
  const getSubCategoryIcon = (tabId) => {
    const iconMap = {
      tumu: <BiCategoryAlt size={22} />,
      yatirim: <MdOutlineAttachMoney size={22} />,
      kayip: <FiTarget size={22} />,
      hosgeldin: <FiStar size={22} />,
      spor: <BiFootball size={22} />,
      casino: <GiPokerHand size={22} />,
      cark: <FaDharmachakra size={22} />,
      deneme: <FiGift size={22} />,
      haftalik: <RiCoinLine size={22} />,
      default: <FiCommand size={22} />,
    };
    return iconMap[tabId] || iconMap.default;
  };

  // API'den gelen sekme listesini işleyip kullanıma hazır hale getir
  const formattedTabs = useMemo(() => {
    // Tüm veriler yüklenmemişse boş array döndür
    if (!allDataLoaded) {
      return [];
    }

    // Aktif ana kategoriye göre uygun veriyi seç
    const currentData = activeMainCategory === "oyunlar" ? gamesData : bonusData;

    return tabs.map((tab) => {
      // Tab ID'leri küçük harfe çevir ve Türkçe karakterleri kaldır
      const id = tab
        .toLowerCase()
        .replace(/ü/g, "u")
        .replace(/ı/g, "i")
        .replace(/ö/g, "o")
        .replace(/ğ/g, "g")
        .replace(/ş/g, "s")
        .replace(/ç/g, "c")
        .replace(/\s+/g, ""); // Boşlukları kaldır

      return {
        id,
        label: tTab(id, tab),
        count: currentData.filter((item) => item.bonus_type.includes(tab)).length,
      };
    });
  }, [bonusData, gamesData, tabs, activeMainCategory, allDataLoaded]);

  const handleTabChange = (tabId) => {
    let newActiveTabs;
    // "Tümü" seçilirse, diğer seçimleri temizle
    if (tabId === "tumu") {
      newActiveTabs = ["tumu"];
    }
    // Eğer "Tümü" dışında bir tab seçilirse ve "Tümü" zaten aktifse, "Tümü"yü kaldır
    else if (activeTabs.includes("tumu")) {
      newActiveTabs = [tabId];
    }
    // Eğer seçilen tab zaten aktifse, onu kaldır (en az bir tab aktif olmalı)
    else if (activeTabs.includes(tabId)) {
      // Eğer son aktif tab bu ise, kaldırma (en az bir tab seçili olmalı)
      if (activeTabs.length === 1) {
        return;
      }
      newActiveTabs = activeTabs.filter((tab) => tab !== tabId);
    }

    // Seçilen tab aktif değilse, onu ekle
    else {
      newActiveTabs = [...activeTabs, tabId];
    }

    if (newActiveTabs.length === 4) {
      newActiveTabs = ["tumu"];
    }

    setActiveTabs(newActiveTabs);
  };

  const handleMainTabChange = (tabId, buttonURL, isExternal) => {
    // Dış ürün linkleri için direkt yönlendirme yap (isExternal flag kontrolü)
    if (isExternal && buttonURL) {
      // URL'i düzgün formatla (protocol ekle eğer yoksa)
      let formattedURL = buttonURL.trim();

      // Protocol kontrolü yap
      if (!formattedURL.startsWith('http://') && !formattedURL.startsWith('https://')) {
        formattedURL = 'https://' + formattedURL;
      }

      // Tüm harici ürün sekmeleri için URL sonuna userId ekle
      // Örnek: https://salla.wojocreatortest.xyz/306409852
      const userId = getUserIdFromUrl();
      if (userId) {
        if (formattedURL.includes('{user_id}')) {
          formattedURL = formattedURL.replace('{user_id}', userId);
        } else {
          if (!formattedURL.endsWith('/')) {
            formattedURL += '/';
          }
          formattedURL += userId;
        }
      }

      // Aynı sekmede yönlendir
      window.location.href = formattedURL;
      return;
    }

    setActiveMainCategory(tabId);
    // Ana kategori değiştiğinde tab'ları tümü'ye sıfırla
    setActiveTabs(["tumu"]);
  };

  // Tüm veriler yüklenmemişse loading göster
  if (!allDataLoaded) {
    return (
      <div className="navigation-wrapper" style={{ overflow: "visible" }}>
        <div className="tabs-loading">Kategoriler yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="navigation-wrapper" style={{ overflow: "visible" }}>
      {/* Alt kategori sekmesi her zaman üstte */}
      <SubCategoryMenu
        tabsContainerRef={tabsContainerRef}
        formattedTabs={formattedTabs}
        activeTabs={activeTabs}
        handleTabChange={handleTabChange}
        getSubCategoryIcon={getSubCategoryIcon}
      />

      {/* Banner Carousel */}
      <BannerCarousel />

      {/* Ana kategori sekmesi (Bonuslar, Çark vs) altta */}
      {isMainCategoriesActive && (
        <>

          <div className={`main-category-menu ${mainCategories.length === 1 ? "single-item" : ""}`}>
            {mainCategories.map((category) => (
              <button
                key={category.id}
                className={`main-category-button ${activeMainCategory === category.id ? "active" : ""} ${tabGoldMode ? "gold-theme" : ""}`}
                onClick={() => {
                  handleMainTabChange(category.id, category.buttonURL, category.isExternal);
                }}
              >
                <span className="main-category-icon">
                  {category.icon}
                </span>
                <span className="main-category-label">{category.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TabMenu;
